import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../api";
import { createMatchDay, updateMatchDay } from "../api/matchDay";
import { getPhases, getSeasonsByTeam } from "../api/seasons";
import { useCurrentTeam } from "../hooks/useCurrentTeam";
import { useCurrentPlayer } from "../hooks/useCurrentPlayer";
import { capitalizeWords, formatFullName } from '../utils/nameUtils';
import { formatDate, formatTime } from '../utils/dateUtils';
import 'font-awesome/css/font-awesome.min.css';
import "./Dashboard.css";
import MatchHistory from "../components/MatchHistory";

function Dashboard() {
    const navigate = useNavigate();
    const { team, loading: teamLoading } = useCurrentTeam();
    const { player: currentPlayer } = useCurrentPlayer();

    const [matches, setMatches] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('create');
    const [selectedMatchId, setSelectedMatchId] = useState(null);
    const [formData, setFormData] = useState({ date: '', time: '', opponentName: '', location: '', phase: '' });
    const [saving, setSaving] = useState(false);
    const [phases, setPhases] = useState([]);
    const [currentSeasonId, setCurrentSeasonId] = useState(null);
    const [currentSeasonName, setCurrentSeasonName] = useState('');

    const teamId = team?.id;
    const canManage = ['ROLE_ADMIN', 'ROLE_COORDINATOR', 'ROLE_CAPTAIN'].includes(currentPlayer?.role || currentPlayer?.player?.role);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const promises = [
                getPhases(),
                teamId ? apiClient.get(`/teams/${teamId}/matchdays`) : Promise.resolve(null),
                teamId ? apiClient.get(`/teams/${teamId}/players`)   : Promise.resolve(null),
                teamId ? getSeasonsByTeam(teamId)                    : Promise.resolve([]),
            ];
            const [phaseData, matchesRes, playersRes, seasonsData] =
                await Promise.all(promises);

            setPhases(phaseData || []);
            if (matchesRes) setMatches(matchesRes.data || []);
            if (playersRes) setPlayers(playersRes.data || []);
            if (seasonsData?.length > 0) {
                setCurrentSeasonId(seasonsData[0].id);
                setCurrentSeasonName(seasonsData[0].name);
            }
        } catch (err) {
            console.error(err);
            if (teamId) setError("Error al cargar los datos del equipo.");
        } finally {
            setLoading(false);
        }
    }, [teamId]);

    useEffect(() => {
        if (!teamLoading) {
            loadData();
        }
    }, [teamLoading, loadData]);

    const PHASE_TIMELINE = ['PRIMERA_FASE', 'SEGUNDA_FASE_A', 'SEGUNDA_FASE_B', 'MASTER_FINAL'];

    const activePhasePoints = (() => {
        const byPhase = matches.reduce((acc, m) => {
            if (!m.phase) return acc;
            if (!acc[m.phase]) acc[m.phase] = [];
            acc[m.phase].push(m);
            return acc;
        }, {});

        let currentPhase = null;
        for (const phase of PHASE_TIMELINE) {
            if (byPhase[phase]?.length > 0) currentPhase = phase;
        }

        if (!currentPhase) return 0;

        return (byPhase[currentPhase] || [])
            .filter(m => m.pointsTeam != null)
            .reduce((sum, m) => sum + (m.pointsTeam || 0), 0);
    })();

    const sortedMatches = [...matches].sort((a, b) => new Date(a.date) - new Date(b.date));
    const now = new Date();
    const futureMatches = sortedMatches.filter(m => new Date(m.date + 'T' + (m.time || '00:00')) >= now);
    const nextMatch = futureMatches.length > 0 ? futureMatches[0] : null;
    const upcomingMatches = sortedMatches.filter(m => m.id !== nextMatch?.id && new Date(m.date) >= now);

    const handleOpenCreate = () => {
        setDialogMode('create');
        setFormData({ date: '', time: '20:00', opponentName: '', location: '', phase: '' });
        setOpenDialog(true);
    };

    const handleOpenEdit = (match) => {
        setDialogMode('edit');
        setSelectedMatchId(match.id);
        setFormData({
            date: match.date.split('T')[0],
            time: match.time,
            opponentName: match.opponentName,
            location: match.location,
            phase: match.phase || '',
        });
        setOpenDialog(true);
    };

    const handleSaveMatch = async (e) => {
        e.preventDefault();
        if (!teamId) return;
        setSaving(true);
        try {
            const payload = {
                ...formData,
                teamId,
                seasonId: currentSeasonId || null,
                phase: formData.phase || null,
            };
            if (dialogMode === 'create') await createMatchDay(payload);
            else await updateMatchDay(selectedMatchId, payload);
            await loadData();
            setOpenDialog(false);
        } catch (e) {
            alert("Error: " + (e.response?.data?.message || e.message));
        } finally {
            setSaving(false);
        }
    };

    const playerDisplayName = (p) => formatFullName(p.name, p.lastName) || capitalizeWords(p.name || 'Jugador');

    const getInitials = (name) => {
        const parts = name.split(" ");
        return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
    };

    const formatCompactName = (name, lastName) => {
        if (!name) return 'Jugador';
        const firstInitial = lastName ? ` ${lastName.charAt(0)}.` : '';
        return `${name}${firstInitial}`;
    };

    if (loading || teamLoading) {
        return (
            <div className="dashboard-wrapper">
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>Cargando panel...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-header">
                <div className="dashboard-title">
                    {teamId ? (
                        <>
                            <h1>{team.name} Categoría</h1>
                            <span className="subtitle">Panel de Gestión</span>
                        </>
                    ) : (
                        <h1>Bienvenida</h1>
                    )}
                </div>
                <div className="dashboard-meta">
                    <div className="season-badge">Temporada 2026</div>
                    {teamId && (
                        <div className="points-pill">
                            Puntos: {activePhasePoints}
                        </div>
                    )}
                </div>
            </div>

            {!teamId && !error && (
                <div className="info-banner-modern">
                    <i className="fa fa-info-circle"></i>
                    <span>No tienes equipo asignado todavía, pero puedes apuntarte a las Americanas de abajo.</span>
                </div>
            )}

            {error && <div className="error-banner">{error}</div>}

            <div className="dashboard-grid">
                <div className="left-column">
                    {teamId && (
                        <div className="match-card">
                            <div className="card-top">
                                <span className="card-label">Próximo Partido</span>
                                {canManage && nextMatch && (
                                    <button className="icon-action" onClick={() => handleOpenEdit(nextMatch)}>
                                        <i className="fa fa-pencil"></i>
                                    </button>
                                )}
                            </div>

                            {nextMatch ? (
                                <div className="match-content">
                                    <div className="match-info">
                                        <div className="match-date-badge">
                                            <span className="day">{formatDate(nextMatch.date, { day: 'numeric' })}</span>
                                            <span className="month">{formatDate(nextMatch.date, { month: 'short' }).toUpperCase()}</span>
                                        </div>
                                        <h2 className="opponent-title">vs {nextMatch.opponentName}</h2>
                                        <div className="match-meta">
                                            <div className="meta-item"><i className="fa fa-clock-o"></i> {nextMatch.time}</div>
                                            <div className="meta-item"><i className="fa fa-map-marker"></i> {nextMatch.location || 'Pista Central'}</div>
                                        </div>
                                    </div>
                                    <button className="btn-hero" onClick={() => navigate(`/matchdays/${nextMatch.id}`)}>
                                        Convocatoria <i className="fa fa-arrow-right"></i>
                                    </button>
                                </div>
                            ) : (
                                <div className="empty-hero">
                                    <i className="fa fa-trophy" style={{fontSize:'3rem', marginBottom:'16px'}}></i>
                                    <p>No hay partidos de liga próximos</p>
                                    {canManage && (
                                        <button className="btn-text" style={{color:'white'}} onClick={handleOpenCreate}>+ Crear Partido</button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {teamId && (
                        <div className="upcoming-card">
                            <div className="section-header">
                                <h3>Calendario de Liga</h3>
                                {canManage && (
                                    <button className="btn-text" onClick={handleOpenCreate}>
                                        <i className="fa fa-plus"></i> Nuevo
                                    </button>
                                )}
                            </div>
                            <div className="match-list">
                                {upcomingMatches.length > 0 ? (
                                    upcomingMatches.map(m => (
                                        <div key={m.id} className="match-row" onClick={() => navigate(`/matchdays/${m.id}`)}>
                                            <div className="date-box">{formatDate(m.date, {day: 'numeric', month: 'numeric'})}</div>
                                            <div className="match-row-info">
                                                <span className="match-row-opponent">vs {m.opponentName}</span>
                                                <span className="match-row-time">{m.time} - {m.location}</span>
                                            </div>
                                            {canManage && (
                                                <button className="btn-icon-row" onClick={(e) => { e.stopPropagation(); handleOpenEdit(m); }}>
                                                    <i className="fa fa-pencil"></i>
                                                </button>
                                            )}
                                            <i className="fa fa-chevron-right" style={{fontSize:'0.8rem', color:'#ccc'}}></i>
                                        </div>
                                    ))
                                ) : (
                                    <p style={{textAlign:'center', color:'#999', fontSize:'0.9rem'}}>No hay más partidos programados.</p>
                                )}
                            </div>
                        </div>
                    )}
                    <MatchHistory
                        prefetchedMatches={matches}
                        prefetchedPhases={phases}
                        prefetchedSeasonName={currentSeasonName}
                        prefetchedSeasonId={currentSeasonId}
                        parentLoading={loading}
                    />
                </div>

                {teamId && (
                    <div className="right-column">
                        <div className="stats-card">
                            <div className="section-header">
                                <h3>Plantilla y Estadísticas</h3>
                            </div>
                            <div className="table-responsive">
                                <table className="custom-table">
                                    <thead>
                                    <tr>
                                        <th>Jugadora</th>
                                        <th style={{textAlign:'center'}}>DISP</th>
                                        <th style={{textAlign:'center'}}>PJ</th>
                                        <th style={{textAlign:'center'}}>Pts</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {players.map(p => {
                                        const compactName = formatCompactName(p.name, p.lastName);
                                        const fullName = formatFullName(p.name, p.lastName);
                                        return (
                                            <tr key={p.id} onClick={() => navigate(`/players/${p.id}`)} style={{cursor:'pointer'}}>
                                                <td>
                                                    <div className="player-cell">
                                                        <div className="avatar-circle compact" >{getInitials(compactName)}</div>
                                                        <span className="player-name-text">{compactName}</span>
                                                    </div>
                                                </td>
                                                <td style={{textAlign:'center'}}>{p.availability || 0}</td>
                                                <td style={{textAlign:'center', fontWeight:'bold'}}>{p.gamesPlayed || 0}</td>
                                                <td style={{textAlign:'center'}}>
                                                    <span className="points-badge">{p.points || 0}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {openDialog && (
                <div className="modal-backdrop">
                    <div className="modal-content">
                        <h3>{dialogMode === 'create' ? 'Nuevo Partido' : 'Editar Partido'}</h3>
                        <form onSubmit={handleSaveMatch} className="modal-form">
                            <div className="form-group">
                                <label>Rival</label>
                                <input type="text" className="form-control" value={formData.opponentName} onChange={e => setFormData({...formData, opponentName: e.target.value})} required />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Fecha</label>
                                    <input type="date" className="form-control" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                                </div>
                                <div className="form-group">
                                    <label>Hora</label>
                                    <input type="time" className="form-control" value={formData.time} onChange={e => setFormData({...formData, time: e.target.value})} required />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Ubicación</label>
                                <input type="text" className="form-control" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
                            </div>
                            {phases.length > 0 && (
                                <div className="form-group">
                                    <label>Fase</label>
                                    <select
                                        className="form-control"
                                        value={formData.phase}
                                        onChange={e => setFormData({...formData, phase: e.target.value})}
                                    >
                                        <option value="">Sin fase</option>
                                        {phases.map(p => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="btn-text" onClick={() => setOpenDialog(false)}>Cancelar</button>
                                <button type="submit" className="btn-submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;